import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from './Button'
import { emptyStateClass } from './index'
import { describeError, stateRegionClass, statusHint } from './kit'

/** Names what failed (`title`, the page's own words), why (server detail or status) and offers Retry. */
export function ErrorState({
  title,
  error,
  onRetry,
  retrying = false,
  region = false,
  className = '',
  localizeDetail,
}: {
  title: string
  error?: unknown
  localizeDetail?: (detail: string) => string
  onRetry?: () => void
  retrying?: boolean
  region?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const facts = describeError(error)
  const hint = statusHint(facts.status)
  const hintText = { forbidden: t('uiKit.forbidden'), notFound: t('uiKit.notFound'), serverFailed: t('uiKit.serverFailed') }
  const why = facts.noResponse ? t('uiKit.noResponse') : ((facts.detail && localizeDetail ? localizeDetail(facts.detail) : facts.detail) ?? (hint ? hintText[hint] : null))
  const alert = (
    <div
      role="alert"
      className={`flex flex-wrap items-start gap-x-3 gap-y-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 ${className}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        {(why || facts.status !== null) && (
          <p className="mt-0.5 break-words">
            {why}
            {facts.status !== null && (
              <span className="text-red-700 dark:text-red-300">
                {why ? ' ' : ''}({t('uiKit.status', { status: facts.status })})
              </span>
            )}
          </p>
        )}
      </div>
      {onRetry && (
        <Button size="sm" onClick={onRetry} loading={retrying}>
          {t('uiKit.retry')}
        </Button>
      )}
    </div>
  )
  return region ? <div className={stateRegionClass}>{alert}</div> : alert
}

/** Nothing to show yet: say so, and offer the action that would fill it when there is one. */
export function EmptyState({
  title,
  action,
  region = false,
  className = '',
}: {
  title: ReactNode
  action?: ReactNode
  region?: boolean
  className?: string
}) {
  return (
    <div className={`${emptyStateClass} ${region ? stateRegionClass : ''} ${className}`}>
      <p>{title}</p>
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  )
}

const SKELETON_WIDTHS = ['w-11/12', 'w-4/5', 'w-2/3', 'w-3/4', 'w-1/2']

/** `block` draws skeleton rows shaped like a list; `inline` is a spinner and a word for a small area. */
export function LoadingState({
  label,
  rows = 3,
  variant = 'block',
  region = false,
  className = '',
}: {
  label?: string
  rows?: number
  variant?: 'block' | 'inline'
  region?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const text = label ?? t('action.loading')
  if (variant === 'inline') {
    return (
      <span role="status" className={`inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
        {text}
      </span>
    )
  }
  return (
    <div role="status" aria-busy="true" className={`space-y-2.5 p-3 ${region ? stateRegionClass : ''} ${className}`}>
      <span className="sr-only">{text}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          aria-hidden
          className={`h-4 animate-pulse rounded bg-slate-200 motion-reduce:animate-none dark:bg-slate-700 ${SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}`}
        />
      ))}
    </div>
  )
}
